import { useEffect, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Dialog } from '@opendoor/atoms/dialog';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { globalObservability } from '@opendoor/observability/slim';
import { COSMOS_URL } from 'components/globals';
import { useFeatureFlagEvaluator } from 'components/helpers/hooks';
import { toAppDeepLinkUrl } from 'components/marketplace/helpers/appDeepLink';
import { QRCodeSVG } from 'qrcode.react';

const APP_DOWNLOAD_URL = 'https://opendoor.app.link/website';
const APPLE_STORE_LINK = 'https://apps.apple.com/us/app/opendoor-buy-sell-homes/id1143708068';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';

const APP_STORE_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?service=cosmos';
const GOOGLE_PLAY_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?service=cosmos';

const QR_SIZE = 96;

const TITLE_DESKTOP = 'Get the Opendoor app to book your self-tour.';
const DESCRIPTION_DESKTOP =
  'Self-tours run from the Opendoor app. Once you’re in, unlock the door at the home with one tap.';
const TITLE_MOBILE = 'Tour any home, on your schedule';
const DESCRIPTION_MOBILE = 'Self-tours run from the Opendoor app. Install it to continue.';

const TITLE_TWO_QR = 'Tour this home on your schedule.';

/**
 * Kill switch for the listing-scoped two-QR layout. Off or unresolved renders
 * the app-download-only layout that predates it.
 */
export const TWO_QR_HANDOFF_FLAG = 'buyer-self-tour-two-qr-handoff';

export type SelfTourAppHandoffPlacement = 'footer' | 'lightbox' | 'main';

const buildAttributedUrl = (baseUrl: string, content: string, addressToken?: string) => {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', 'marketplace_pdp');
  url.searchParams.set('utm_medium', 'app_handoff_modal');
  url.searchParams.set('utm_content', content);
  if (addressToken) {
    url.searchParams.set('utm_term', addressToken);
  }
  return url.toString();
};

/**
 * QR value that opens this listing in the buyer app. The app routes
 * `/properties/{slug}/aid_{addressToken}` universal links to the in-app PDP;
 * a phone without the app falls through to the mobile-web PDP.
 */
export const buildOpenAppQrUrl = ({
  slug,
  addressToken,
  placement,
}: {
  slug: string;
  addressToken: string;
  placement: SelfTourAppHandoffPlacement;
}) =>
  toAppDeepLinkUrl(
    buildAttributedUrl(
      `${COSMOS_URL}/properties/${slug}/aid_${addressToken}`,
      `${placement}-qr-open-app`,
      addressToken,
    ),
  );

const StepCard = ({
  step,
  title,
  caption,
  emphasized,
  analyticsName,
  children,
}: {
  step: string;
  title: string;
  caption: string;
  emphasized?: boolean;
  analyticsName: string;
  children: React.ReactNode;
}) => (
  <Col
    className="flex-1 items-start gap-4 rounded-2xl border border-od-border-primary bg-od-bg-secondary p-4"
    analyticsName={analyticsName}
    trackImpression
  >
    <Box
      className={`size-8 shrink-0 items-center justify-center rounded-lg ${
        emphasized ? 'bg-od-bg-accent-brand' : 'bg-od-bg-hover-primary'
      }`}
    >
      <Text
        as="span"
        className={`text-label-xl ${
          emphasized ? 'text-od-content-inverse-primary' : 'text-od-content-primary'
        }`}
      >
        {step}
      </Text>
    </Box>
    <Col className="w-full flex-1 gap-1">
      <Text as="span" className="text-label-xl text-od-content-primary">
        {title}
      </Text>
      <Text as="p" className="text-body-md text-od-content-secondary">
        {caption}
      </Text>
    </Col>
    {children}
  </Col>
);

const QrImage = ({ value, label }: { value: string; label: string }) => (
  <Box
    className="items-center justify-center rounded-lg border border-od-border-primary bg-od-bg-primary p-1.5"
    aria-label={label}
    role="img"
  >
    <QRCodeSVG value={value} size={QR_SIZE} />
  </Box>
);

export const SelfTourAppHandoffModal = ({
  isOpen,
  onClose,
  placement,
  addressToken,
  slug,
}: {
  isOpen: boolean;
  onClose: () => void;
  placement: SelfTourAppHandoffPlacement;
  addressToken?: string;
  slug?: string;
}) => {
  const evaluateFlag = useFeatureFlagEvaluator();
  const [isTwoQrEnabled, setIsTwoQrEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const enabled = await evaluateFlag(TWO_QR_HANDOFF_FLAG);
        if (!cancelled) {
          setIsTwoQrEnabled(enabled);
        }
      } catch (error) {
        // A failed flag read keeps the app-download-only layout.
        globalObservability.getSentryClient().captureException?.(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [evaluateFlag]);

  const appleStoreUrl = buildAttributedUrl(APPLE_STORE_LINK, placement, addressToken);
  const googlePlayUrl = buildAttributedUrl(GOOGLE_PLAY_LINK, placement, addressToken);

  const openAppQrUrl =
    slug && addressToken ? buildOpenAppQrUrl({ slug, addressToken, placement }) : null;
  const showTwoQr = isTwoQrEnabled && openAppQrUrl !== null;

  const title = showTwoQr ? TITLE_TWO_QR : TITLE_DESKTOP;

  const renderStoreBadges = (variant: 'standalone' | 'inCard') => {
    const badges = (
      [
        {
          href: appleStoreUrl,
          analyticsName: `self-tour-app-handoff-app-store-badge-${placement}`,
          alt: 'Download on the App Store',
          src: APP_STORE_BADGE_URL,
        },
        {
          href: googlePlayUrl,
          analyticsName: `self-tour-app-handoff-google-play-badge-${placement}`,
          alt: 'Get it on Google Play',
          src: GOOGLE_PLAY_BADGE_URL,
        },
      ] as const
    ).map((badge) => (
      <Box
        key={badge.alt}
        href={badge.href}
        external
        analyticsName={badge.analyticsName}
        aria-label={badge.alt}
        trackImpression
        className={variant === 'standalone' ? 'flex-1 md:flex-none' : 'w-31'}
      >
        <Image
          src={badge.src}
          alt={badge.alt}
          className={
            variant === 'standalone'
              ? 'h-11 w-full object-contain md:w-35'
              : 'h-10 w-full object-contain object-left'
          }
        />
      </Box>
    ));
    return variant === 'standalone' ? (
      <Row className="w-full gap-1.5 pt-1">{badges}</Row>
    ) : (
      <Col className="gap-3">{badges}</Col>
    );
  };

  return (
    <Dialog.Root
      role="dialog"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog>
        <Dialog.Title className="sr-only">{title}</Dialog.Title>
        <Dialog.Description className="sr-only">{DESCRIPTION_DESKTOP}</Dialog.Description>
        {showTwoQr ? (
          <Col className="w-full gap-4 md:w-165">
            <Text as="span" className="text-subheader-md text-od-content-primary md:hidden">
              {TITLE_MOBILE}
            </Text>
            <Text as="span" className="hidden text-subheader-md text-od-content-primary md:block">
              {TITLE_TWO_QR}
            </Text>
            <Text as="p" className="text-body-md text-od-content-secondary md:hidden">
              {DESCRIPTION_MOBILE}
            </Text>
            <Text as="p" className="hidden text-body-md text-od-content-secondary md:block">
              {DESCRIPTION_DESKTOP}
            </Text>
            <Row className="hidden gap-3 md:flex md:items-stretch">
              <StepCard
                step="1"
                title="Get the Opendoor app"
                caption="Already have the app? Skip to step 2."
                analyticsName={`self-tour-app-handoff-get-app-qr-${placement}`}
              >
                <Row className="w-full items-center gap-4">
                  <QrImage
                    value={buildAttributedUrl(
                      APP_DOWNLOAD_URL,
                      `${placement}-qr-get-app`,
                      addressToken,
                    )}
                    label="QR code to download the Opendoor app"
                  />
                  {renderStoreBadges('inCard')}
                </Row>
              </StepCard>
              <StepCard
                step="2"
                title="Book your tour in the app"
                caption="Scan to open this listing."
                emphasized
                analyticsName={`self-tour-app-handoff-open-app-qr-${placement}`}
              >
                <QrImage
                  value={openAppQrUrl}
                  label="QR code to open this listing in the Opendoor app"
                />
              </StepCard>
            </Row>
            <Box className="md:hidden">{renderStoreBadges('standalone')}</Box>
          </Col>
        ) : (
          <Col className="w-full gap-4 md:w-127.5 md:flex-row md:items-stretch">
            <Col className="flex-1 justify-between gap-4">
              <Col className="gap-4">
                <Text as="span" className="text-subheader-md text-od-content-primary md:hidden">
                  {TITLE_MOBILE}
                </Text>
                <Text
                  as="span"
                  className="hidden text-subheader-md text-od-content-primary md:block"
                >
                  {TITLE_DESKTOP}
                </Text>
                <Text as="p" className="text-body-md text-od-content-secondary md:hidden">
                  {DESCRIPTION_MOBILE}
                </Text>
                <Text as="p" className="hidden text-body-md text-od-content-secondary md:block">
                  {DESCRIPTION_DESKTOP}
                </Text>
              </Col>
              {renderStoreBadges('standalone')}
            </Col>
            <Col
              className="hidden items-center justify-center gap-2.5 rounded-xl bg-od-bg-secondary p-5 md:flex"
              aria-label="QR code to download the Opendoor app"
              role="img"
            >
              <Box className="items-center justify-center rounded-md bg-od-bg-primary p-1.5">
                <QRCodeSVG
                  value={buildAttributedUrl(APP_DOWNLOAD_URL, placement, addressToken)}
                  size={QR_SIZE}
                />
              </Box>
              <Text as="p" className="max-w-30 text-center text-body-sm text-od-content-primary">
                Scan with your phone camera
              </Text>
            </Col>
          </Col>
        )}
      </Dialog>
    </Dialog.Root>
  );
};
